import colors from 'tailwindcss/colors';
import {
  bark,
  crimson,
  crimsonDark,
  forest,
  neutral,
  plum,
  plumDark,
  red,
  redDark,
  sand,
  skies,
  sunlight,
  tangerine,
  tangerineDark,
  tomato,
  tomatoDark,
} from './primitive-colors';

type SemanticColor = { label: string; color: string };

/**
 * Status indicator glyph colors — theme-aware. Each token corresponds 1:1 with
 * a `StatusIndicatorStatus` value and is consumed directly inside the SVG via
 * `fill="hsl(var(--twc-status-icon-<status>))"` (the `hsl(...)` wrapper is
 * required because `tw-colors` stores each variable as raw HSL components —
 * e.g. `35 38% 76%` — rather than a complete color).
 *
 * Light themes (`light` / `newBrand`) use the saturated mid-tone step from each
 * palette. Dark themes (`dark` / `newBrandDark`) follow Radix conventions:
 * Radix scales (tomato / tangerine) swap to the matching `*Dark[10]` step;
 * Heidi scales (sand / skies / forest / sunlight / bark) invert toward the
 * lighter end of the scale so the glyph still pops on a `bg-entity-{color}-muted`
 * surface that has itself flipped to a dark shade.
 *
 * The decorative `marker` variants (`dot`, `clover`, `star`, `incircle`) paint
 * with `currentColor` driven by `markerColor`, so they don't appear here —
 * see `markerIconColorsLight` / `markerIconColorsDark` below.
 */
const statusIconColorsLight = {
  'status-icon-empty': { label: 'sand/700', color: sand[700] },
  'status-icon-to-do': { label: 'plum/10', color: plum[10] },
  'status-icon-in-progress': { label: 'skies/600', color: skies[600] },
  'status-icon-in-review': { label: 'tangerine/10', color: tangerine[10] },
  'status-icon-ready': { label: 'forest/600', color: forest[600] },
  'status-icon-done': { label: 'sunlight/700', color: sunlight[700] },
  'status-icon-alert': { label: 'tomato/10', color: tomato[10] },
  'status-icon-archive': { label: 'bark/950', color: bark[950] },
} as const;

const statusIconColorsDark = {
  'status-icon-empty': { label: 'sand/300', color: sand[300] },
  'status-icon-to-do': { label: 'plumDark/10', color: plumDark[10] },
  'status-icon-in-progress': { label: 'skies/400', color: skies[400] },
  'status-icon-in-review': { label: 'tangerineDark/10', color: tangerineDark[10] },
  'status-icon-ready': { label: 'forest/400', color: forest[400] },
  'status-icon-done': { label: 'sunlight/300', color: sunlight[300] },
  'status-icon-alert': { label: 'tomatoDark/10', color: tomatoDark[10] },
  'status-icon-archive': { label: 'bark/200', color: bark[200] },
} as const;

/**
 * Marker glyph colours — theme-aware. Light themes (`light` / `newBrand`) use
 * the saturated mid-tone step from each palette so a `StatusIndicator
 * marker="dot" markerColor="blue"` reads at the same intensity as
 * `status="in-progress"`. Dark themes (`dark` / `newBrandDark`) follow Radix
 * conventions: Radix scales (tomato / tangerine / plum / crimson) swap to the
 * matching `*Dark[10]` step; Heidi scales (sand / skies / forest / sunlight)
 * invert toward the lighter end of the scale so the glyph still pops on a
 * `bg-entity-{color}-muted` surface that has itself flipped to a dark shade.
 *
 * Consumed by `markerColorClasses` in `status-indicator.styles.ts` via the
 * `text-marker-icon-{color}` Tailwind class; the glyph SVG paints with
 * `fill="currentColor"` so the wrapper's text colour flows into the fill.
 */
const markerIconColorsLight = {
  'marker-icon-neutral': { label: 'sand/700', color: sand[700] },
  'marker-icon-red': { label: 'tomato/10', color: tomato[10] },
  'marker-icon-orange': { label: 'tangerine/10', color: tangerine[10] },
  'marker-icon-yellow': { label: 'sunlight/700', color: sunlight[700] },
  'marker-icon-green': { label: 'forest/600', color: forest[600] },
  'marker-icon-blue': { label: 'skies/600', color: skies[600] },
  'marker-icon-purple': { label: 'plum/10', color: plum[10] },
  'marker-icon-pink': { label: 'crimson/10', color: crimson[10] },
} as const;

const markerIconColorsDark = {
  'marker-icon-neutral': { label: 'sand/300', color: sand[300] },
  'marker-icon-red': { label: 'tomatoDark/10', color: tomatoDark[10] },
  'marker-icon-orange': { label: 'tangerineDark/10', color: tangerineDark[10] },
  'marker-icon-yellow': { label: 'sunlight/300', color: sunlight[300] },
  'marker-icon-green': { label: 'forest/400', color: forest[400] },
  'marker-icon-blue': { label: 'skies/400', color: skies[400] },
  'marker-icon-purple': { label: 'plumDark/10', color: plumDark[10] },
  'marker-icon-pink': { label: 'crimsonDark/10', color: crimsonDark[10] },
} as const;

const semanticThemes = {
  newBrand: {
    ...statusIconColorsLight,
    ...markerIconColorsLight,
    'text-primary': {
      label: 'bark/950',
      color: bark[950],
    },
    'text-primary-invert': {
      label: 'white',
      color: colors.white,
    },
    'text-secondary': {
      label: 'bark/600',
      color: bark[600],
    },
    'text-disabled': {
      label: 'bark/200',
      color: bark[200],
    },
    'text-tertiary': {
      label: 'bark/500',
      color: bark[500],
    },
    'text-active': {
      label: 'skies/700',
      color: skies[700],
    },
    'text-link': {
      label: 'skies/700',
      color: skies[700],
    },
    'text-link-hover': {
      label: 'skies/800',
      color: skies[800],
    },
    'text-link-pressed': {
      label: 'skies/800',
      color: skies[800],
    },
    'text-primary-on-dark': {
      label: 'white',
      color: colors.white,
    },
    'text-primary-on-light': {
      label: 'bark/950',
      color: bark[950],
    },
    'text-secondary-on-dark': {
      label: 'neutral/300',
      color: neutral[300],
    },
    'text-secondary-on-light': {
      label: 'bark/600',
      color: bark[600],
    },
    'background-primary': {
      label: 'bark/800',
      color: bark[800],
    },
    'background-primary-hover': {
      label: 'bark/900',
      color: bark[900],
    },
    'background-primary-pressed': {
      label: 'bark/950',
      color: bark[950],
    },
    'background-primary-disabled': {
      label: 'bark/200',
      color: bark[200],
    },
    'background-primary-invert': {
      label: 'white',
      color: colors.white,
    },
    'background-secondary': {
      label: 'sand/150',
      color: sand[150],
    },
    'background-secondary-hover': {
      label: 'sand/200',
      color: sand[200],
    },
    'background-secondary-pressed': {
      label: 'sand/300',
      color: sand[300],
    },
    'background-secondary-disabled': {
      label: 'sand/75',
      color: sand[75],
    },
    'background-tertiary': {
      label: 'sand/50',
      color: sand[50],
    },
    'background-tertiary-hover': {
      label: 'sand/75',
      color: sand[75],
    },
    'background-tertiary-pressed': {
      label: 'sand/150',
      color: sand[150],
    },
    'background-tertiary-disabled': {
      label: 'sand/50',
      color: sand[50],
    },
    'callout-background': {
      label: 'bark/800',
      color: bark[800],
    },
    'background-button-primary': {
      label: 'bark/800',
      color: bark[800],
    },
    'background-button-primary-hover': {
      label: 'bark/900',
      color: bark[900],
    },
    'background-button-primary-pressed': {
      label: 'bark/950',
      color: bark[950],
    },
    'background-button-primary-disabled': {
      label: 'bark/200',
      color: bark[200],
    },
    'background-switch-on': {
      label: 'bark/800',
      color: bark[800],
    },
    'background-switch-on-disabled': {
      label: 'bark/200',
      color: bark[200],
    },
    'background-switch-off': {
      label: 'sand/300',
      color: sand[300],
    },
    'background-switch-off-disabled': {
      label: 'sand/150',
      color: sand[150],
    },
    'background-switch-thumb': {
      label: 'white',
      color: colors.white,
    },
    'background-evidence-selected': {
      label: 'skies/600',
      color: skies[600],
    },
    surface: {
      label: 'white',
      color: colors.white,
    },
    'surface-elevated': {
      label: 'white',
      color: colors.white,
    },
    'surface-hover': {
      label: 'sand/50',
      color: sand[50],
    },
    'surface-pressed': {
      label: 'sand/75',
      color: sand[75],
    },
    'surface-page': {
      label: 'sand/25',
      color: sand[25],
    },
    'surface-disabled': {
      label: 'sand/50',
      color: sand[50],
    },
    'surface-accent': {
      label: 'sand/50',
      color: sand[50],
    },
    'surface-accent-hover': {
      label: 'sand/75',
      color: sand[75],
    },
    'surface-accent-active': {
      label: 'sand/150',
      color: sand[150],
    },
    'surface-accent-active-hover': {
      label: 'sand/200',
      color: sand[200],
    },
    'surface-selected': {
      label: 'skies/25',
      color: skies[25],
    },
    'surface-selected-hover': {
      label: 'skies/50',
      color: skies[50],
    },
    'surface-selected-pressed': {
      label: 'skies/100',
      color: skies[100],
    },
    'surface-container': {
      label: 'sand/15',
      color: sand[15],
    },
    'surface-shell': {
      label: 'white',
      color: colors.white,
    },
    // Overlay edge; = `border` in light, steps up in dark where `border` collapses onto `surface-elevated`.
    'surface-elevated-border': {
      label: 'sand/150',
      color: sand[150],
    },
    border: {
      label: 'sand/150',
      color: sand[150],
    },
    'border-hover': {
      label: 'sand/300',
      color: sand[300],
    },
    'border-pressed': {
      label: 'sand/500',
      color: sand[500],
    },
    'border-disabled': {
      label: 'sand/100',
      color: sand[100],
    },
    'border-strong': {
      label: 'bark/600',
      color: bark[600],
    },
    'border-strong-hover': {
      label: 'bark/700',
      color: bark[700],
    },
    'border-strong-pressed': {
      label: 'bark/700',
      color: bark[700],
    },
    'border-strong-disabled': {
      label: 'bark/400',
      color: bark[400],
    },
    'border-selected': {
      label: 'bark/950',
      color: bark[950],
    },
    'border-focus': {
      label: 'skies/700',
      color: skies[700],
    },
    'validation-success': {
      label: 'green/600',
      color: colors.green[600],
    },
    'validation-success-hover': {
      label: 'green/700',
      color: colors.green[700],
    },
    'validation-success-pressed': {
      label: 'green/800',
      color: colors.green[800],
    },
    'validation-success-disabled': {
      label: 'green/400 50%',
      color: `${colors.green[400]}80`,
    },
    'validation-success-muted': {
      label: 'green/50',
      color: colors.green[50],
    },
    'validation-success-border': {
      label: 'green/100',
      color: colors.green[100],
    },
    'validation-destructive': {
      label: 'red/600',
      color: colors.red[600],
    },
    'validation-destructive-hover': {
      label: 'red/700',
      color: colors.red[700],
    },
    'validation-destructive-pressed': {
      label: 'red/800',
      color: colors.red[800],
    },
    'validation-destructive-disabled': {
      label: 'red/600 50%',
      color: `${colors.red[600]}80`,
    },
    'validation-destructive-muted': {
      label: 'red/50',
      color: colors.red[50],
    },
    'validation-destructive-border': {
      label: 'red/100',
      color: colors.red[100],
    },
    'validation-warning': {
      label: 'orange/600',
      color: colors.orange[600],
    },
    'validation-warning-hover': {
      label: 'orange/700',
      color: colors.orange[700],
    },
    'validation-warning-pressed': {
      label: 'orange/800',
      color: colors.orange[800],
    },
    'validation-warning-disabled': {
      label: 'orange/600 50%',
      color: `${colors.orange[600]}80`,
    },
    'validation-warning-muted': {
      label: 'orange/50',
      color: colors.orange[50],
    },
    'validation-warning-border': {
      label: 'orange/100',
      color: colors.orange[100],
    },
    /* @deprecated info retirement; see docs/design-docs/tier-color-migration.md */
    'validation-info': {
      label: 'skies/500',
      color: skies[500],
    },
    'validation-info-hover': {
      label: 'skies/600',
      color: skies[600],
    },
    'validation-info-pressed': {
      label: 'skies/700',
      color: skies[700],
    },
    'validation-info-disabled': {
      label: 'skies/500 50%',
      color: `${skies[500]}80`,
    },
    'validation-info-muted': {
      label: 'skies/25',
      color: skies[25],
    },
    'validation-info-border': {
      label: 'skies/100',
      color: skies[100],
    },
    /* DS-1202 — the reworked semantic tier (`semantic-{property}-{tone}-{state}`,
     * taxonomy: Concept → Property → Variant → Modifier) + component tiers
     * (`button-background-*`, `toast-background-*`). FINAL values; consumed
     * exclusively by the *.experimental.styles.ts recipes behind
     * `release-experimental-design-system-forms`. Legacy `validation-*` stays
     * untouched until the app-side codemod at flag cleanup. */
    'semantic-background-success': {
      label: 'forest/50',
      color: forest[50],
    },
    'semantic-background-success-hover': {
      label: 'forest/100',
      color: forest[100],
    },
    'semantic-background-success-active': {
      label: 'forest/200',
      color: forest[200],
    },
    'semantic-border-success': {
      label: 'forest/200',
      color: forest[200],
    },
    'semantic-border-success-strong': {
      label: 'forest/500',
      color: forest[500],
    },
    'semantic-text-success': {
      label: 'forest/700',
      color: forest[700],
    },
    'semantic-text-success-active': {
      label: 'forest/800',
      color: forest[800],
    },
    'semantic-fill-success': {
      label: 'forest/500',
      color: forest[500],
    },
    'semantic-background-destructive': {
      label: 'red/3',
      color: red[3],
    },
    'semantic-background-destructive-hover': {
      label: 'red/3',
      color: red[3],
    },
    'semantic-background-destructive-active': {
      label: 'red/4',
      color: red[4],
    },
    'semantic-border-destructive': {
      label: 'red/4',
      color: red[4],
    },
    'semantic-border-destructive-strong': {
      label: 'red/9',
      color: red[9],
    },
    // Held to 3:1 (WCAG 1.4.11) — its own token rather than an alias of
    // `semantic-border-destructive-strong`, so a field edge can move alone.
    'control-border-invalid': {
      label: 'red/9',
      color: red[9],
    },
    'control-border-invalid-hover': {
      label: 'red/10',
      color: red[10],
    },
    'control-border-focus': {
      label: 'bark/400',
      color: bark[400],
    },
    'semantic-text-destructive': {
      label: 'red/11',
      color: red[11],
    },
    'semantic-text-destructive-active': {
      label: 'red/12',
      color: red[12],
    },
    'semantic-fill-destructive': {
      label: 'red/9',
      color: red[9],
    },
    'semantic-background-warning': {
      label: 'tangerine/3',
      color: tangerine[3],
    },
    'semantic-background-warning-hover': {
      label: 'tangerine/3',
      color: tangerine[3],
    },
    'semantic-background-warning-active': {
      label: 'tangerine/4',
      color: tangerine[4],
    },
    'semantic-border-warning': {
      label: 'tangerine/4',
      color: tangerine[4],
    },
    'semantic-border-warning-strong': {
      label: 'tangerine/9',
      color: tangerine[9],
    },
    'semantic-text-warning': {
      label: 'tangerine/11 oklch-darkened (AA; DS-1265)',
      color: '#af4304',
    },
    'semantic-text-warning-active': {
      label: 'tangerine/12',
      color: tangerine[12],
    },
    'semantic-fill-warning': {
      label: 'tangerine/9',
      color: tangerine[9],
    },
    /* @deprecated info retirement; see docs/design-docs/tier-color-migration.md */
    'semantic-background-info': {
      label: 'skies/25',
      color: skies[25],
    },
    'semantic-background-info-hover': {
      label: 'skies/25',
      color: skies[25],
    },
    'semantic-background-info-active': {
      label: 'skies/50',
      color: skies[50],
    },
    /* @deprecated info retirement; see docs/design-docs/tier-color-migration.md */
    'semantic-border-info': {
      label: 'skies/100',
      color: skies[100],
    },
    'semantic-border-info-strong': {
      label: 'skies/500',
      color: skies[500],
    },
    /* @deprecated info retirement; see docs/design-docs/tier-color-migration.md */
    'semantic-text-info': {
      label: 'skies/700',
      color: skies[700],
    },
    'semantic-text-info-active': {
      label: 'skies/700',
      color: skies[700],
    },
    /* @deprecated info retirement; see docs/design-docs/tier-color-migration.md */
    'semantic-fill-info': {
      label: 'skies/500',
      color: skies[500],
    },
    'button-background-success': {
      label: 'forest/600',
      color: forest[600],
    },
    'button-background-success-hover': {
      label: 'forest/700',
      color: forest[700],
    },
    'button-background-success-active': {
      label: 'forest/800',
      color: forest[800],
    },
    'button-background-destructive': {
      label: 'red/11',
      color: red[11],
    },
    'button-background-destructive-hover': {
      label: 'red/11 forest-cadence (DS-1265)',
      color: '#b7061d',
    },
    'button-background-destructive-active': {
      label: 'red/11 forest-cadence (DS-1265)',
      color: '#8f0013',
    },
    'button-background-warning': {
      label: 'tangerine/11',
      color: tangerine[11],
    },
    'button-background-warning-hover': {
      label: 'tangerine/11 forest-cadence (DS-1265)',
      color: '#af4200',
    },
    'button-background-warning-active': {
      label: 'tangerine/11 forest-cadence (DS-1265)',
      color: '#8a3200',
    },
    /* @deprecated info retirement; see docs/design-docs/tier-color-migration.md */
    'button-background-info': {
      label: 'skies/600',
      color: skies[600],
    },
    'button-background-info-hover': {
      label: 'skies/700',
      color: skies[700],
    },
    'button-background-info-active': {
      label: 'skies/800',
      color: skies[800],
    },
    'toast-background-success': {
      label: 'forest/500',
      color: forest[500],
    },
    'toast-background-destructive': {
      label: 'red/9',
      color: red[9],
    },
    'toast-background-warning': {
      label: 'tangerine/9',
      color: tangerine[9],
    },
    /* @deprecated info retirement; see docs/design-docs/tier-color-migration.md */
    'toast-background-info': {
      label: 'skies/500',
      color: skies[500],
    },
    'accent-forest': {
      label: 'forest/500',
      color: forest[500],
    },
    'accent-forest-hover': {
      label: 'forest/600',
      color: forest[600],
    },
    'accent-sunlight': {
      label: 'sunlight/300',
      color: sunlight[300],
    },
    'accent-sunlight-hover': {
      label: 'sunlight/400',
      color: sunlight[400],
    },
    'accent-sky': {
      label: 'skies/200',
      color: skies[200],
    },
    'accent-sky-hover': {
      label: 'skies/200',
      color: skies[200],
    },
    'accent-red': {
      label: 'red/500',
      color: colors.red[500],
    },
    'accent-red-hover': {
      label: 'red/600',
      color: colors.red[600],
    },
    'accent-orange': {
      label: 'orange/500',
      color: colors.orange[500],
    },
    'accent-orange-hover': {
      label: 'orange/600',
      color: colors.orange[600],
    },
    'accent-amber': {
      label: 'amber/500',
      color: colors.amber[500],
    },
    'accent-amber-hover': {
      label: 'amber/600',
      color: colors.amber[600],
    },
    'accent-yellow': {
      label: 'yellow/500',
      color: colors.yellow[500],
    },
    'accent-yellow-hover': {
      label: 'yellow/600',
      color: colors.yellow[600],
    },
    'accent-lime': {
      label: 'lime/500',
      color: colors.lime[500],
    },
    'accent-lime-hover': {
      label: 'lime/600',
      color: colors.lime[600],
    },
    'accent-green': {
      label: 'green/500',
      color: colors.green[500],
    },
    'accent-green-hover': {
      label: 'green/600',
      color: colors.green[600],
    },
    'accent-teal': {
      label: 'teal/500',
      color: colors.teal[500],
    },
    'accent-teal-hover': {
      label: 'teal/600',
      color: colors.teal[600],
    },
    'accent-cyan': {
      label: 'cyan/500',
      color: colors.cyan[500],
    },
    'accent-cyan-hover': {
      label: 'cyan/600',
      color: colors.cyan[600],
    },
    'accent-blue': {
      label: 'blue/500',
      color: colors.blue[500],
    },
    'accent-blue-hover': {
      label: 'blue/600',
      color: colors.blue[600],
    },
    'accent-purple': {
      label: 'purple/500',
      color: colors.purple[500],
    },
    'accent-purple-hover': {
      label: 'purple/600',
      color: colors.purple[600],
    },
    'accent-pink': {
      label: 'pink/500',
      color: colors.pink[500],
    },
    'accent-pink-hover': {
      label: 'pink/600',
      color: colors.pink[600],
    },
    'tier-pro': {
      label: '#978AEA',
      color: '#978AEA',
    },
    'tier-pro-text': {
      label: '#5D4EC5',
      color: '#5D4EC5',
    },
    'tier-pro-muted': {
      label: '#F8F7FF',
      color: '#F8F7FF',
    },
    'tier-pro-border': {
      label: '#E5E2FF',
      color: '#E5E2FF',
    },
    'tier-pro-hover': {
      label: '#7E70DB',
      color: '#7E70DB',
    },
    'tier-pro-disabled': {
      label: '#978AEA 50%',
      color: '#978AEA80',
    },
    'tier-practice': {
      label: '#EA8743',
      color: '#EA8743',
    },
    'tier-practice-text': {
      label: '#DD610B',
      color: '#DD610B',
    },
    'tier-practice-muted': {
      label: '#FFFAF7',
      color: '#FFFAF7',
    },
    'tier-practice-border': {
      label: '#FFE6D5',
      color: '#FFE6D5',
    },
    'tier-practice-hover': {
      label: '#D46D25',
      color: '#D46D25',
    },
    'tier-practice-disabled': {
      label: '#EA8743 50%',
      color: '#EA874380',
    },
    'entity-red-muted': { label: 'tomato/4', color: tomato[4] },
    'entity-red-background': { label: 'tomato/4', color: tomato[4] },
    'entity-red-foreground': { label: 'tomato/12', color: tomato[12] },
    'entity-red-disabled-muted': { label: 'tomato/4 50%', color: `${tomato[4]}80` },
    'entity-red-disabled-foreground': {
      label: 'tomato/12 50%',
      color: `${tomato[12]}80`,
    },
    'entity-orange-muted': { label: 'tangerine/4', color: tangerine[4] },
    'entity-orange-background': { label: 'tangerine/4', color: tangerine[4] },
    'entity-orange-foreground': { label: 'tangerine/12', color: tangerine[12] },
    'entity-orange-disabled-muted': {
      label: 'tangerine/4 50%',
      color: `${tangerine[4]}80`,
    },
    'entity-orange-disabled-foreground': {
      label: 'tangerine/12 50%',
      color: `${tangerine[12]}80`,
    },
    'entity-purple-muted': { label: 'plum/4', color: plum[4] },
    'entity-purple-background': { label: 'plum/4', color: plum[4] },
    'entity-purple-foreground': { label: 'plum/12', color: plum[12] },
    'entity-purple-disabled-muted': { label: 'plum/4 50%', color: `${plum[4]}80` },
    'entity-purple-disabled-foreground': {
      label: 'plum/12 50%',
      color: `${plum[12]}80`,
    },
    'entity-pink-muted': { label: 'crimson/4', color: crimson[4] },
    'entity-pink-background': { label: 'crimson/4', color: crimson[4] },
    'entity-pink-foreground': { label: 'crimson/12', color: crimson[12] },
    'entity-pink-disabled-muted': { label: 'crimson/4 50%', color: `${crimson[4]}80` },
    'entity-pink-disabled-foreground': {
      label: 'crimson/12 50%',
      color: `${crimson[12]}80`,
    },
    'entity-blue-muted': { label: 'skies/100', color: skies[100] },
    'entity-blue-background': { label: 'skies/200', color: skies[200] },
    'entity-blue-foreground': { label: 'skies/900', color: skies[900] },
    'entity-blue-disabled-muted': { label: 'skies/100 50%', color: `${skies[100]}80` },
    'entity-blue-disabled-foreground': {
      label: 'skies/900 50%',
      color: `${skies[900]}80`,
    },
    'entity-green-muted': { label: 'forest/100', color: forest[100] },
    'entity-green-background': { label: 'forest/200', color: forest[200] },
    'entity-green-foreground': { label: 'forest/900', color: forest[900] },
    'entity-green-disabled-muted': {
      label: 'forest/100 50%',
      color: `${forest[100]}80`,
    },
    'entity-green-disabled-foreground': {
      label: 'forest/900 50%',
      color: `${forest[900]}80`,
    },
    'entity-yellow-muted': { label: 'sunlight/200', color: sunlight[200] },
    'entity-yellow-background': { label: 'sunlight/200', color: sunlight[200] },
    'entity-yellow-foreground': { label: 'sunlight/900', color: sunlight[900] },
    'entity-yellow-disabled-muted': {
      label: 'sunlight/200 50%',
      color: `${sunlight[200]}80`,
    },
    'entity-yellow-disabled-foreground': {
      label: 'sunlight/900 50%',
      color: `${sunlight[900]}80`,
    },
    'entity-neutral-muted': { label: 'sand/100', color: sand[100] },
    'entity-neutral-background': { label: 'sand/200', color: sand[200] },
    'entity-neutral-foreground': { label: 'sand/900', color: sand[900] },
    'entity-neutral-disabled-muted': { label: 'sand/100 50%', color: `${sand[100]}80` },
    'entity-neutral-disabled-foreground': {
      label: 'sand/900 50%',
      color: `${sand[900]}80`,
    },
  },
  newBrandDark: {
    ...statusIconColorsDark,
    ...markerIconColorsDark,
    'text-primary': {
      label: 'white',
      color: colors.white,
    },
    'text-primary-invert': {
      label: 'black',
      color: colors.black,
    },
    'text-secondary': {
      label: 'neutral/300',
      color: neutral[300],
    },
    'text-disabled': {
      label: 'neutral/500',
      color: neutral[500],
    },
    'text-tertiary': {
      label: 'neutral/400',
      color: neutral[400],
    },
    'text-active': {
      label: 'skies/300',
      color: skies[300],
    },
    'text-link': {
      label: 'skies/300',
      color: skies[300],
    },
    'text-link-hover': {
      label: 'skies/200',
      color: skies[200],
    },
    'text-link-pressed': {
      label: 'skies/200',
      color: skies[200],
    },
    'text-primary-on-dark': {
      label: 'white',
      color: colors.white,
    },
    'text-primary-on-light': {
      label: 'bark/950',
      color: bark[950],
    },
    'text-secondary-on-dark': {
      label: 'neutral/300',
      color: neutral[300],
    },
    'text-secondary-on-light': {
      label: 'bark/600',
      color: bark[600],
    },
    'background-primary': {
      label: 'neutral/400',
      color: neutral[400],
    },
    'background-primary-hover': {
      label: 'neutral/300',
      color: neutral[300],
    },
    'background-primary-pressed': {
      label: 'neutral/200',
      color: neutral[200],
    },
    'background-primary-disabled': {
      label: 'neutral/700',
      color: neutral[700],
    },
    'background-primary-invert': {
      label: 'bark/800',
      color: bark[800],
    },
    'background-secondary': {
      label: 'neutral/800',
      color: neutral[800],
    },
    'background-secondary-hover': {
      label: 'neutral/700',
      color: neutral[700],
    },
    'background-secondary-pressed': {
      label: 'neutral/600',
      color: neutral[600],
    },
    'background-secondary-disabled': {
      label: 'neutral/600',
      color: neutral[600],
    },
    'background-tertiary': {
      label: 'neutral/800',
      color: neutral[800],
    },
    'background-tertiary-hover': {
      label: 'neutral/700',
      color: neutral[700],
    },
    'background-tertiary-pressed': {
      label: 'neutral/600',
      color: neutral[600],
    },
    'background-tertiary-disabled': {
      label: 'neutral/600',
      color: neutral[600],
    },
    'callout-background': {
      label: 'neutral/500',
      color: neutral[500],
    },
    'background-button-primary': {
      label: 'skies/300',
      color: skies[300],
    },
    'background-button-primary-hover': {
      label: 'skies/200',
      color: skies[200],
    },
    'background-button-primary-pressed': {
      label: 'skies/100',
      color: skies[100],
    },
    'background-button-primary-disabled': {
      label: 'skies/300 50%',
      color: `${skies[300]}80`,
    },
    'background-switch-on': {
      label: 'skies/400',
      color: skies[400],
    },
    'background-switch-on-disabled': {
      label: 'skies/800',
      color: skies[800],
    },
    'background-switch-off': {
      label: 'neutral/500',
      color: neutral[500],
    },
    'background-switch-off-disabled': {
      label: 'neutral/700',
      color: neutral[700],
    },
    'background-switch-thumb': {
      label: 'white',
      color: colors.white,
    },
    'background-evidence-selected': {
      label: 'skies/400',
      color: skies[400],
    },
    surface: {
      label: 'neutral/900',
      color: neutral[900],
    },
    'surface-elevated': {
      label: 'neutral/850',
      color: neutral[850],
    },
    'surface-hover': {
      label: 'neutral/800',
      color: neutral[800],
    },
    'surface-pressed': {
      label: 'neutral/800',
      color: neutral[800],
    },
    'surface-page': {
      label: 'black',
      color: colors.black,
    },
    'surface-disabled': {
      label: 'neutral/800',
      color: neutral[800],
    },
    'surface-accent': {
      label: 'neutral/900',
      color: neutral[900],
    },
    'surface-accent-hover': {
      label: 'neutral/800',
      color: neutral[800],
    },
    'surface-accent-active': {
      label: 'neutral/850',
      color: neutral[850],
    },
    'surface-accent-active-hover': {
      label: 'neutral/750',
      color: neutral[750],
    },
    'surface-selected': {
      label: 'skies/600 30%',
      color: `${skies[600]}4D`,
    },
    'surface-selected-hover': {
      label: 'skies/800',
      color: skies[800],
    },
    'surface-selected-pressed': {
      label: 'skies/700',
      color: skies[700],
    },
    'surface-container': {
      label: 'neutral/850',
      color: neutral[850],
    },
    'surface-shell': {
      label: 'black',
      color: colors.black,
    },
    // Steps up from `border` (neutral/800), which is itself near-indistinguishable from the
    // neutral/850 surface it edges; a stronger edge keeps overlays visible in dark.
    'surface-elevated-border': {
      label: 'neutral/750',
      color: neutral[750],
    },
    border: {
      label: 'neutral/800',
      color: neutral[800],
    },
    'border-hover': {
      label: 'neutral/700',
      color: neutral[700],
    },
    'border-pressed': {
      label: 'neutral/600',
      color: neutral[600],
    },
    'border-disabled': {
      label: 'neutral/850',
      color: neutral[850],
    },
    'border-strong': {
      label: 'neutral/300',
      color: neutral[300],
    },
    'border-strong-hover': {
      label: 'neutral/200',
      color: neutral[200],
    },
    'border-strong-pressed': {
      label: 'neutral/200',
      color: neutral[200],
    },
    'border-strong-disabled': {
      label: 'neutral/300',
      color: neutral[300],
    },
    'border-selected': {
      label: 'white',
      color: colors.white,
    },
    'border-focus': {
      label: 'skies/500',
      color: skies[500],
    },
    'validation-success': {
      label: 'green/400',
      color: colors.green[400],
    },
    'validation-success-hover': {
      label: 'green/300',
      color: colors.green[300],
    },
    'validation-success-pressed': {
      label: 'green/200',
      color: colors.green[200],
    },
    'validation-success-disabled': {
      label: 'green/400 50%',
      color: `${colors.green[400]}80`,
    },
    'validation-success-muted': {
      label: 'green/900',
      color: colors.green[900],
    },
    'validation-success-border': {
      label: 'green/800',
      color: colors.green[800],
    },
    'validation-destructive': {
      label: 'red/400',
      color: colors.red[400],
    },
    'validation-destructive-hover': {
      label: 'red/300',
      color: colors.red[300],
    },
    'validation-destructive-pressed': {
      label: 'red/200',
      color: colors.red[200],
    },
    'validation-destructive-disabled': {
      label: 'red/400 50%',
      color: `${colors.red[400]}80`,
    },
    'validation-destructive-muted': {
      label: 'red/950',
      color: colors.red[950],
    },
    'validation-destructive-border': {
      label: 'red/800',
      color: colors.red[800],
    },
    'validation-warning': {
      label: 'orange/400',
      color: colors.orange[400],
    },
    'validation-warning-hover': {
      label: 'orange/300',
      color: colors.orange[300],
    },
    'validation-warning-pressed': {
      label: 'orange/200',
      color: colors.orange[200],
    },
    'validation-warning-disabled': {
      label: 'orange/400 50%',
      color: `${colors.orange[400]}80`,
    },
    'validation-warning-muted': {
      label: 'orange/800',
      color: colors.orange[800],
    },
    'validation-warning-border': {
      label: 'orange/700',
      color: colors.orange[700],
    },
    /* @deprecated info retirement; see docs/design-docs/tier-color-migration.md */
    'validation-info': {
      label: 'skies/400',
      color: skies[400],
    },
    'validation-info-hover': {
      label: 'skies/300',
      color: skies[300],
    },
    'validation-info-pressed': {
      label: 'skies/200',
      color: skies[200],
    },
    'validation-info-disabled': {
      label: 'skies/400 50%',
      color: `${skies[400]}80`,
    },
    'validation-info-muted': {
      label: 'skies/950',
      color: skies[950],
    },
    'validation-info-border': {
      label: 'skies/800',
      color: skies[800],
    },
    /* DS-1202 — the reworked semantic tier (`semantic-{property}-{tone}-{state}`,
     * taxonomy: Concept → Property → Variant → Modifier) + component tiers
     * (`button-background-*`, `toast-background-*`). FINAL values; consumed
     * exclusively by the *.experimental.styles.ts recipes behind
     * `release-experimental-design-system-forms`. Legacy `validation-*` stays
     * untouched until the app-side codemod at flag cleanup. */
    'semantic-background-success': {
      label: 'forest/950',
      color: forest[950],
    },
    'semantic-background-success-hover': {
      label: 'forest/900',
      color: forest[900],
    },
    'semantic-background-success-active': {
      label: 'forest/800',
      color: forest[800],
    },
    'semantic-border-success': {
      label: 'forest/800',
      color: forest[800],
    },
    'semantic-border-success-strong': {
      label: 'forest/500',
      color: forest[500],
    },
    'semantic-text-success': {
      label: 'forest/300',
      color: forest[300],
    },
    'semantic-text-success-active': {
      label: 'forest/300',
      color: forest[300],
    },
    'semantic-fill-success': {
      label: 'forest/500',
      color: forest[500],
    },
    'semantic-background-destructive': {
      label: 'redDark/2',
      color: redDark[2],
    },
    'semantic-background-destructive-hover': {
      label: 'redDark/3',
      color: redDark[3],
    },
    'semantic-background-destructive-active': {
      label: 'redDark/4',
      color: redDark[4],
    },
    'semantic-border-destructive': {
      label: 'redDark/5',
      color: redDark[5],
    },
    'semantic-border-destructive-strong': {
      label: 'redDark/9',
      color: redDark[9],
    },
    // Held to 3:1 (WCAG 1.4.11) — its own token rather than an alias of
    // `semantic-border-destructive-strong`, so a field edge can move alone.
    'control-border-invalid': {
      label: 'redDark/9',
      color: redDark[9],
    },
    'control-border-invalid-hover': {
      label: 'redDark/10',
      color: redDark[10],
    },
    'control-border-focus': {
      label: 'neutral/500',
      color: neutral[500],
    },
    'semantic-text-destructive': {
      label: 'redDark/11',
      color: redDark[11],
    },
    'semantic-text-destructive-active': {
      label: 'redDark/11',
      color: redDark[11],
    },
    'semantic-fill-destructive': {
      label: 'redDark/9',
      color: redDark[9],
    },
    'semantic-background-warning': {
      label: 'tangerineDark/2',
      color: tangerineDark[2],
    },
    'semantic-background-warning-hover': {
      label: 'tangerineDark/3',
      color: tangerineDark[3],
    },
    'semantic-background-warning-active': {
      label: 'tangerineDark/4',
      color: tangerineDark[4],
    },
    'semantic-border-warning': {
      label: 'tangerineDark/5',
      color: tangerineDark[5],
    },
    'semantic-border-warning-strong': {
      label: 'tangerineDark/9',
      color: tangerineDark[9],
    },
    'semantic-text-warning': {
      label: 'tangerineDark/11',
      color: tangerineDark[11],
    },
    'semantic-text-warning-active': {
      label: 'tangerineDark/11',
      color: tangerineDark[11],
    },
    'semantic-fill-warning': {
      label: 'tangerineDark/9',
      color: tangerineDark[9],
    },
    /* @deprecated info retirement; see docs/design-docs/tier-color-migration.md */
    'semantic-background-info': {
      label: 'skies/950',
      color: skies[950],
    },
    'semantic-background-info-hover': {
      label: 'skies/900',
      color: skies[900],
    },
    'semantic-background-info-active': {
      label: 'skies/800',
      color: skies[800],
    },
    /* @deprecated info retirement; see docs/design-docs/tier-color-migration.md */
    'semantic-border-info': {
      label: 'skies/900',
      color: skies[900],
    },
    'semantic-border-info-strong': {
      label: 'skies/500',
      color: skies[500],
    },
    /* @deprecated info retirement; see docs/design-docs/tier-color-migration.md */
    'semantic-text-info': {
      label: 'skies/200',
      color: skies[200],
    },
    'semantic-text-info-active': {
      label: 'skies/200',
      color: skies[200],
    },
    /* @deprecated info retirement; see docs/design-docs/tier-color-migration.md */
    'semantic-fill-info': {
      label: 'skies/500',
      color: skies[500],
    },
    'button-background-success': {
      label: 'forest/500',
      color: forest[500],
    },
    'button-background-success-hover': {
      label: 'forest/400',
      color: forest[400],
    },
    'button-background-success-active': {
      label: 'forest/300',
      color: forest[300],
    },
    'button-background-destructive': {
      label: 'redDark/9',
      color: redDark[9],
    },
    'button-background-destructive-hover': {
      label: 'redDark/10',
      color: redDark[10],
    },
    'button-background-destructive-active': {
      label: 'redDark/11',
      color: redDark[11],
    },
    'button-background-warning': {
      label: 'tangerineDark/9',
      color: tangerineDark[9],
    },
    'button-background-warning-hover': {
      label: 'tangerineDark/10',
      color: tangerineDark[10],
    },
    'button-background-warning-active': {
      label: 'tangerineDark/11',
      color: tangerineDark[11],
    },
    /* @deprecated info retirement; see docs/design-docs/tier-color-migration.md */
    'button-background-info': {
      label: 'skies/500',
      color: skies[500],
    },
    'button-background-info-hover': {
      label: 'skies/400',
      color: skies[400],
    },
    'button-background-info-active': {
      label: 'skies/300',
      color: skies[300],
    },
    'toast-background-success': {
      label: 'forest/500',
      color: forest[500],
    },
    'toast-background-destructive': {
      label: 'redDark/9',
      color: redDark[9],
    },
    'toast-background-warning': {
      label: 'tangerineDark/9',
      color: tangerineDark[9],
    },
    /* @deprecated info retirement; see docs/design-docs/tier-color-migration.md */
    'toast-background-info': {
      label: 'skies/500',
      color: skies[500],
    },
    'accent-forest': {
      label: 'forest/500',
      color: forest[500],
    },
    'accent-forest-hover': {
      label: 'forest/600',
      color: forest[600],
    },
    'accent-sunlight': {
      label: 'sunlight/300',
      color: sunlight[300],
    },
    'accent-sunlight-hover': {
      label: 'sunlight/400',
      color: sunlight[400],
    },
    'accent-sky': {
      label: 'skies/200',
      color: skies[200],
    },
    'accent-sky-hover': {
      label: 'skies/200',
      color: skies[200],
    },
    'accent-red': {
      label: 'red/400',
      color: colors.red[400],
    },
    'accent-red-hover': {
      label: 'red/300',
      color: colors.red[300],
    },
    'accent-orange': {
      label: 'orange/400',
      color: colors.orange[400],
    },
    'accent-orange-hover': {
      label: 'orange/300',
      color: colors.orange[300],
    },
    'accent-amber': {
      label: 'amber/400',
      color: colors.amber[400],
    },
    'accent-amber-hover': {
      label: 'amber/300',
      color: colors.amber[300],
    },
    'accent-yellow': {
      label: 'yellow/400',
      color: colors.yellow[400],
    },
    'accent-yellow-hover': {
      label: 'yellow/300',
      color: colors.yellow[300],
    },
    'accent-lime': {
      label: 'lime/400',
      color: colors.lime[400],
    },
    'accent-lime-hover': {
      label: 'lime/300',
      color: colors.lime[300],
    },
    'accent-green': {
      label: 'green/400',
      color: colors.green[400],
    },
    'accent-green-hover': {
      label: 'green/300',
      color: colors.green[300],
    },
    'accent-teal': {
      label: 'teal/400',
      color: colors.teal[400],
    },
    'accent-teal-hover': {
      label: 'teal/300',
      color: colors.teal[300],
    },
    'accent-cyan': {
      label: 'cyan/400',
      color: colors.cyan[400],
    },
    'accent-cyan-hover': {
      label: 'cyan/300',
      color: colors.cyan[300],
    },
    'accent-blue': {
      label: 'blue/400',
      color: colors.blue[400],
    },
    'accent-blue-hover': {
      label: 'blue/300',
      color: colors.blue[300],
    },
    'accent-purple': {
      label: 'purple/400',
      color: colors.purple[400],
    },
    'accent-purple-hover': {
      label: 'purple/300',
      color: colors.purple[300],
    },
    'accent-pink': {
      label: 'pink/400',
      color: colors.pink[400],
    },
    'accent-pink-hover': {
      label: 'pink/300',
      color: colors.pink[300],
    },
    'tier-pro': {
      label: '#A188D7',
      color: '#A188D7',
    },
    'tier-pro-text': {
      label: '#A188D7',
      color: '#A188D7',
    },
    'tier-pro-muted': {
      label: '#382367',
      color: '#382367',
    },
    'tier-pro-border': {
      label: '#4A3580',
      color: '#4A3580',
    },
    'tier-pro-hover': {
      label: '#B7A3E0',
      color: '#B7A3E0',
    },
    'tier-pro-disabled': {
      label: '#A188D7 50%',
      color: '#A188D780',
    },
    'tier-practice': {
      label: '#EA9357',
      color: '#EA9357',
    },
    'tier-practice-text': {
      label: '#EA9357',
      color: '#EA9357',
    },
    'tier-practice-muted': {
      label: '#563219',
      color: '#563219',
    },
    'tier-practice-border': {
      label: '#6B3A1A',
      color: '#6B3A1A',
    },
    'tier-practice-hover': {
      label: '#EEA877',
      color: '#EEA877',
    },
    'tier-practice-disabled': {
      label: '#EA9357 50%',
      color: '#EA935780',
    },
    'entity-red-muted': { label: 'tomatoDark/4', color: tomatoDark[4] },
    'entity-red-background': { label: 'tomatoDark/4', color: tomatoDark[4] },
    'entity-red-foreground': { label: 'tomatoDark/12', color: tomatoDark[12] },
    'entity-red-disabled-muted': {
      label: 'tomatoDark/4 50%',
      color: `${tomatoDark[4]}80`,
    },
    'entity-red-disabled-foreground': {
      label: 'tomatoDark/12 50%',
      color: `${tomatoDark[12]}80`,
    },
    'entity-orange-muted': { label: 'tangerineDark/4', color: tangerineDark[4] },
    'entity-orange-background': { label: 'tangerineDark/4', color: tangerineDark[4] },
    'entity-orange-foreground': {
      label: 'tangerineDark/12',
      color: tangerineDark[12],
    },
    'entity-orange-disabled-muted': {
      label: 'tangerineDark/4 50%',
      color: `${tangerineDark[4]}80`,
    },
    'entity-orange-disabled-foreground': {
      label: 'tangerineDark/12 50%',
      color: `${tangerineDark[12]}80`,
    },
    'entity-purple-muted': { label: 'plumDark/4', color: plumDark[4] },
    'entity-purple-background': { label: 'plumDark/4', color: plumDark[4] },
    'entity-purple-foreground': { label: 'plumDark/12', color: plumDark[12] },
    'entity-purple-disabled-muted': {
      label: 'plumDark/4 50%',
      color: `${plumDark[4]}80`,
    },
    'entity-purple-disabled-foreground': {
      label: 'plumDark/12 50%',
      color: `${plumDark[12]}80`,
    },
    'entity-pink-muted': { label: 'crimsonDark/4', color: crimsonDark[4] },
    'entity-pink-background': { label: 'crimsonDark/4', color: crimsonDark[4] },
    'entity-pink-foreground': { label: 'crimsonDark/12', color: crimsonDark[12] },
    'entity-pink-disabled-muted': {
      label: 'crimsonDark/4 50%',
      color: `${crimsonDark[4]}80`,
    },
    'entity-pink-disabled-foreground': {
      label: 'crimsonDark/12 50%',
      color: `${crimsonDark[12]}80`,
    },
    'entity-blue-muted': { label: 'skies/900', color: skies[900] },
    'entity-blue-background': { label: 'skies/900', color: skies[900] },
    'entity-blue-foreground': { label: 'skies/200', color: skies[200] },
    'entity-blue-disabled-muted': { label: 'skies/900 50%', color: `${skies[900]}80` },
    'entity-blue-disabled-foreground': {
      label: 'skies/200 50%',
      color: `${skies[200]}80`,
    },
    'entity-green-muted': { label: 'forest/800', color: forest[800] },
    'entity-green-background': { label: 'forest/900', color: forest[900] },
    'entity-green-foreground': { label: 'forest/200', color: forest[200] },
    'entity-green-disabled-muted': {
      label: 'forest/800 50%',
      color: `${forest[800]}80`,
    },
    'entity-green-disabled-foreground': {
      label: 'forest/200 50%',
      color: `${forest[200]}80`,
    },
    'entity-yellow-muted': { label: 'sunlight/900', color: sunlight[900] },
    'entity-yellow-background': { label: 'sunlight/950', color: sunlight[950] },
    'entity-yellow-foreground': { label: 'sunlight/200', color: sunlight[200] },
    'entity-yellow-disabled-muted': {
      label: 'sunlight/900 50%',
      color: `${sunlight[900]}80`,
    },
    'entity-yellow-disabled-foreground': {
      label: 'sunlight/200 50%',
      color: `${sunlight[200]}80`,
    },
    'entity-neutral-muted': { label: 'sand/900', color: sand[900] },
    'entity-neutral-background': { label: 'sand/900', color: sand[900] },
    'entity-neutral-foreground': { label: 'sand/200', color: sand[200] },
    'entity-neutral-disabled-muted': { label: 'sand/900 50%', color: `${sand[900]}80` },
    'entity-neutral-disabled-foreground': {
      label: 'sand/200 50%',
      color: `${sand[200]}80`,
    },
  },
};

/**
 * `light` and `dark` are legacy: the product remaps them to `newBrand`/
 * `newBrandDark` via next-themes and only ever ships the live pair. Aliasing
 * them means a new token needs only `newBrand` + `newBrandDark` values; the
 * `satisfies` makes one added to `newBrand` but missing from `newBrandDark` a
 * compile error.
 */
export const semanticColors = {
  light: semanticThemes.newBrand,
  dark: semanticThemes.newBrandDark,
  newBrand: semanticThemes.newBrand,
  newBrandDark: semanticThemes.newBrandDark,
} satisfies Record<
  'light' | 'dark' | 'newBrand' | 'newBrandDark',
  Record<keyof typeof semanticThemes.newBrand, SemanticColor>
>;
